import React from "react";
import { View, StyleSheet, type GestureResponderEvent } from "react-native";
import { colors, layout } from "@/constants";
import { Voice } from "@/utils/voices";
import Interactive from "./base/Interactive";
import Text from "./base/Text";
import Icon, { IconName } from "./base/Icon";

/**
 * Props interface for the VoiceListItem component
 */
interface VoiceListItemProps {
  /** Voice configuration object */
  voice: Voice;
  /** Whether this voice is currently selected */
  isSelected: boolean;
  /** Whether this voice sample is currently playing */
  isPlaying: boolean;
  /** Callback when voice is selected */
  onSelect: (voiceId: string) => void;
  /** Callback when play button is pressed */
  onPlay: (voiceId: string) => void;
}

/**
 * List item component for displaying voice options with selection and sample playback.
 * The whole card is a pressable selector; the play button is a nested pressable
 * that stops event propagation so it doesn't also trigger selection.
 *
 * @param props - The component props
 * @returns A list item with voice info, selection state, and play button
 */
export default function VoiceListItem({
  voice,
  isSelected,
  isPlaying,
  onSelect,
  onPlay
}: VoiceListItemProps) {
  const handleSelect = () => onSelect(voice.id);

  const handlePlayPress = (event: GestureResponderEvent) => {
    event.stopPropagation();
    onPlay(voice.id);
  };

  const playIcon: IconName = isPlaying ? "pause_circle" : "play_circle";
  const playIconColor = isPlaying ? colors.tertiaryContainer : colors.primary;

  return (
    <Interactive
      style={[styles.container, isSelected && styles.selectedContainer]}
      onPress={handleSelect}
      accessibilityRole="radio"
      accessibilityState={{ checked: isSelected }}
      accessibilityLabel={`${voice.name} voice`}
    >
      <View style={styles.content}>
        <View style={styles.selectionIndicator}>
          <Icon
            name={
              isSelected ? "radio_button_checked" : "radio_button_unchecked"
            }
            size="large"
            style={{
              color: isSelected ? colors.primary : colors.onSurfaceVariant
            }}
          />
        </View>

        <View style={styles.voiceInfo}>
          <Text variant="semiBold" size="medium" style={styles.voiceName}>
            {voice.name}
          </Text>
          <Text size="small" color="secondary">
            {voice.description}
          </Text>
        </View>

        <Interactive
          style={styles.playButton}
          onPress={handlePlayPress}
          accessibilityLabel={
            isPlaying
              ? `Stop ${voice.name} sample`
              : `Play ${voice.name} sample`
          }
        >
          <Icon
            name={playIcon}
            size="xlarge"
            style={{ color: playIconColor }}
          />
        </Interactive>
      </View>
    </Interactive>
  );
}

const styles = StyleSheet.create({
  container: {
    backgroundColor: colors.surface,
    borderWidth: layout.borderWidth.thin,
    borderColor: colors.outline,
    borderRadius: layout.borderRadius.large,
    marginBottom: layout.space.md
  },
  selectedContainer: {
    borderColor: colors.primary
  },
  content: {
    flexDirection: "row",
    alignItems: "center",
    padding: layout.space.sm
  },
  selectionIndicator: {
    marginRight: layout.space.md
  },
  voiceInfo: {
    flex: 1,
    marginRight: layout.space.sm
  },
  voiceName: {
    marginBottom: layout.space.xs
  },
  playButton: {
    padding: layout.space.xs,
    borderRadius: layout.borderRadius.round
  }
});
